// ─── Colours ───────────────────────────────────────────────────────────────
const C = {
  primary: '#1d4ed8', primaryLight: '#eff6ff', primaryBorder: '#bfdbfe',
  success: '#059669', successLight: '#ecfdf5', successBorder: '#6ee7b7',
  danger: '#dc2626', dangerLight: '#fef2f2', dangerBorder: '#fca5a5',
  warn: '#d97706', warnLight: '#fffbeb', warnBorder: '#fde68a',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray400: '#94a3b8', gray500: '#64748b', gray700: '#374151', gray900: '#0f172a',
};

// ─── Card ───────────────────────────────────────────────────────────────────
export const Card = ({ children, style = {} }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.gray200}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', ...style }}>
    {children}
  </div>
);

export const CardHeader = ({ title, subtitle, action }) => (
  <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.gray100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.gray900 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: C.gray500, marginTop: 2 }}>{subtitle}</div>}
    </div>
    {action && <div>{action}</div>}
  </div>
);

// ─── Stat card ──────────────────────────────────────────────────────────────
export const StatCard = ({ label, value, sub, color = C.primary, Icon }) => (
  <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: `1px solid ${C.gray200}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.gray500, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.gray900, letterSpacing: '-0.5px' }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.gray500, marginTop: 4 }}>{sub}</div>}
      </div>
      {Icon && (
        <div style={{ width: 42, height: 42, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={19} color={color} />
        </div>
      )}
    </div>
  </div>
);

// ─── Page header ────────────────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: C.gray900, letterSpacing: '-0.4px', margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: 13, color: C.gray500, margin: '3px 0 0' }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ─── Button ─────────────────────────────────────────────────────────────────
const BV = {
  primary: { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff', border: 'none' },
  success: { background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', border: 'none' },
  danger:  { background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: '#fff', border: 'none' },
  ghost:   { background: C.gray50, color: C.gray700, border: `1px solid ${C.gray200}` },
  outline: { background: 'transparent', color: C.primary, border: `1px solid ${C.primary}` },
};
const BS = {
  sm: { padding: '6px 12px', fontSize: 12 },
  md: { padding: '9px 18px', fontSize: 13 },
  lg: { padding: '12px 26px', fontSize: 15 },
};
export const Btn = ({ children, onClick, variant = 'primary', size = 'md', disabled, type = 'button', style = {} }) => (
  <button type={type} onClick={onClick} disabled={disabled} style={{
    ...BV[variant], ...BS[size], borderRadius: 9, cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 600, opacity: disabled ? 0.6 : 1, display: 'inline-flex', alignItems: 'center',
    gap: 6, transition: 'opacity 0.15s', ...style
  }}>{children}</button>
);

// ─── Input / Select / Textarea ───────────────────────────────────────────────
const fieldBase = (error) => ({
  width: '100%', padding: '9px 12px', border: `1px solid ${error ? C.danger : C.gray200}`,
  borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  color: C.gray900, background: '#fff', fontFamily: 'inherit',
});

export const Input = ({ label, error, containerStyle = {}, ...props }) => (
  <div style={containerStyle}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.gray700, marginBottom: 5 }}>{label}</label>}
    <input style={fieldBase(error)} {...props} />
    {error && <p style={{ margin: '3px 0 0', fontSize: 11, color: C.danger }}>{error}</p>}
  </div>
);

export const Select = ({ label, error, children, containerStyle = {}, ...props }) => (
  <div style={containerStyle}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.gray700, marginBottom: 5 }}>{label}</label>}
    <select style={{ ...fieldBase(error), cursor: 'pointer' }} {...props}>{children}</select>
    {error && <p style={{ margin: '3px 0 0', fontSize: 11, color: C.danger }}>{error}</p>}
  </div>
);

// ─── Badge ───────────────────────────────────────────────────────────────────
const BColors = {
  blue:   { bg: '#eff6ff', text: '#1d4ed8' },
  green:  { bg: '#ecfdf5', text: '#059669' },
  red:    { bg: '#fef2f2', text: '#dc2626' },
  orange: { bg: '#fff7ed', text: '#c2410c' },
  purple: { bg: '#faf5ff', text: '#7c3aed' },
  gray:   { bg: C.gray100, text: C.gray500 },
};
export const Badge = ({ children, color = 'gray' }) => {
  const c = BColors[color] || BColors.gray;
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, background: c.bg, color: c.text, fontSize: 11, fontWeight: 700 }}>
      {children}
    </span>
  );
};

// ─── Table ───────────────────────────────────────────────────────────────────
export const Table = ({ headers, rows, empty = 'No data found' }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: C.gray50 }}>
          {headers.map((h, i) => (
            <th key={i} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${C.gray200}`, whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={headers.length} style={{ textAlign: 'center', padding: 40, color: C.gray400 }}>{empty}</td></tr>
          : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}
              onMouseEnter={e => e.currentTarget.style.background = C.gray50}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {row.map((cell, j) => <td key={j} style={{ padding: '11px 14px', color: C.gray900, verticalAlign: 'middle' }}>{cell}</td>)}
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const fmt = (val, decimals = 2) => {
  if (val == null || isNaN(val)) return '₹0.00';
  return '₹' + parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const POINT_VALUES = ['0.10', '0.20', '0.50'];
